import React, { useState } from 'react';
import AuthService from '../services/authService';

interface ForgotPasswordFormProps {
  switchToLogin: () => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ switchToLogin }) => {
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!usernameOrEmail.trim()) {
      setMessage({ text: 'Please enter your username or email', type: 'error' });
      return;
    }

    try {
      setIsSubmitting(true);
      const authService = new AuthService();
      const response = await authService.requestPasswordReset(usernameOrEmail);
      
      if (response && (response.status === 200 || response.status === 201)) {
        setMessage({ 
          text: 'Password reset link sent! Please check your email.', 
          type: 'success' 
        });
        setUsernameOrEmail('');
      } else {
        // Safely extract error message as a string
        let errorMsg = "Failed to send reset link. Please try again.";
        
        if (response?.data?.message) {
          errorMsg = typeof response.data.message === 'string' 
            ? response.data.message
            : JSON.stringify(response.data.message);
        } else if (response?.data?.detail) {
          errorMsg = typeof response.data.detail === 'string'
            ? response.data.detail
            : JSON.stringify(response.data.detail);
        } else if (response?.message) {
          errorMsg = typeof response.message === 'string'
            ? response.message
            : JSON.stringify(response.message);
        }
        
        setMessage({ text: errorMsg, type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'An error occurred. Please try again.', type: 'error' });
      console.error('Password reset error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className='flex flex-col items-center w-full max-w-[494px] bg-white p-10 pt-14 rounded-[18px] shadow-lg'>
      <div className="w-full flex flex-col grow">
        <div className="flex flex-col items-center w-full">
          <img
            loading="lazy"
            srcSet="/athlyte_logo.png"
            className="w-[73px] h-[50px]"
            alt="login"
          />
          <p className="mt-1 text-[16px] font-bold text-black">
            Athlyte College Sports Communicator
          </p>
          <p className="mt-1 text-[14px] font-semibold text-black/30">
            Lead the future of sports storytelling
          </p>
        </div>

        <div className="flex flex-col items-center w-full mt-10">
          <p className="text-[20px] font-medium text-[#292929]">
            Reset Your Password
          </p>
          <p className="mt-2 text-[13px] font-normal text-[#5B5B5B]">
            Enter your account details
          </p>
        </div>

        {message && (
          <div className={`w-full p-3 mb-4 mt-4 rounded text-sm font-medium text-center ${
            message.type === 'success'
              ? 'bg-green-100 text-green-700 border border-green-200'
              : 'bg-red-100 text-red-700 border border-red-200'
          }`}>
            {message.text}
          </div>
        )}

        <div className="flex flex-col mt-5 w-full">
          <label htmlFor="usernameOrEmail" className="text-sm font-medium text-neutral-600 mb-1">
            Username or Email
          </label>
          <input
            id="usernameOrEmail"
            type="text"
            value={usernameOrEmail}
            onChange={(e) => setUsernameOrEmail(e.target.value)}
            className="w-full py-2 pl-3 pr-10 bg-[#F6FCFD] focus:bg-[#F6FBFE] focus:border-[#2BACC9] focus:outline-none border-0 focus:border rounded placeholder:text-neutral-400"
            placeholder="Enter your username or email"
          />
        </div>

        <div className="flex flex-col max-md:max-w-full mt-6 justify-center items-center">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex flex-row justify-center items-center cursor-pointer self-stretch px-16 py-3 text-center text-lg text-white whitespace-nowrap bg-cyan-600 rounded disabled:opacity-50"
          >
            {isSubmitting ? 'Sending...' : 'Send Reset Link'}
          </button>
        </div>

        <div className="mt-6 text-center text-sm">
          <button
            type="button"
            onClick={switchToLogin}
            className="text-cyan-700 font-semibold cursor-pointer hover:underline"
          >
            Back to Login
          </button>
        </div>
      </div>
    </form>
  );
};

export default ForgotPasswordForm;
